var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useMemo } from 'react';
import { translate } from '@utils/i18n';
import SearchPanel from '@components/SearchPanel';
import useDebounce from '@hooks/useDebounce';
import Table, { TableSize } from '@components/Table';
import { Loader, LoaderSizes } from '@components/Loader/Loader';
import Select from '@components/v2/Select';
import Brushaway from '@components/Brushaway';
import { namespace } from '../constants';
import { useLongFormTemplateLibraries, useImportLongFormTemplateMutation } from '../hooks/useLongFormTemplatesMutations';
function LongFormTemplateImportModal({ isOpen, onClose }) {
    const [searchText, setSearchText] = useState('');
    const [systemTypeFilter, setSystemTypeFilter] = useState('');
    const searchTextDebounced = useDebounce(searchText);
    const { data, isLoading } = useLongFormTemplateLibraries();
    const importMutation = useImportLongFormTemplateMutation();
    const libraries = (data === null || data === void 0 ? void 0 : data.libraries) || [];
    const systemTypes = (data === null || data === void 0 ? void 0 : data.systemTypes) || [];
    const handleImport = (library) => __awaiter(this, void 0, void 0, function* () {
        try {
            yield importMutation.mutateAsync(library.id);
            onClose();
        }
        catch (error) {
            // Error already handled by mutation hook with toast
        }
    });
    // Filter libraries based on search and system type
    const filteredLibraries = useMemo(() => {
        let filtered = libraries;
        if (searchTextDebounced.trim()) {
            const lower = searchTextDebounced.toLowerCase();
            filtered = filtered.filter((lib) => lib.name.toLowerCase().includes(lower));
        }
        if (systemTypeFilter) {
            filtered = filtered.filter((lib) => lib.systemType === systemTypeFilter);
        }
        return filtered;
    }, [searchTextDebounced, systemTypeFilter, libraries]);
    const columns = useMemo(() => [
        {
            label: translate('import_form.headers.name', { namespace }),
            dataKey: 'name'
        },
        {
            label: translate('import_form.headers.system_type', { namespace }),
            dataKey: 'displaySystemType'
        },
        {
            label: '',
            dataKey: 'id',
            // eslint-disable-next-line react/no-unused-prop-types
            renderer: ({ item }) => (React.createElement("button", { type: "button", className: "qmb-control", onClick: () => {
                    void handleImport(item);
                }, disabled: importMutation.isLoading }, importMutation.isLoading
                ? translate('import_form.importing', { namespace })
                : translate('import_form.import', { namespace })))
        }
    ], [importMutation.isLoading]);
    return (React.createElement(Brushaway, { open: isOpen, title: translate('import_form.title', { namespace }), onClose: onClose },
        React.createElement("div", { className: "filters__group--search mb-3", style: { display: 'flex', gap: 12, alignItems: 'center' } },
            React.createElement(SearchPanel, { value: searchText, placeholder: translate('import_form.search_placeholder', { namespace }), onChange: setSearchText }),
            React.createElement(Select, { label: translate('import_form.all_types', { namespace }), value: systemTypeFilter, options: [{ value: '', label: translate('import_form.all_types', { namespace }) }, ...systemTypes], onChange: setSystemTypeFilter })),
        React.createElement("div", null,
            isLoading && (React.createElement("div", { className: "loading-center" },
                React.createElement(Loader, { size: LoaderSizes.xlarge }))),
            !isLoading && filteredLibraries.length > 0 && (React.createElement(Table, { columns: columns, data: filteredLibraries, size: TableSize.Full })),
            !isLoading && filteredLibraries.length === 0 && (React.createElement("div", { className: "alert alert-info" }, translate('import_form.no_templates', { namespace }))))));
}
export default LongFormTemplateImportModal;
